import type { RouteRecordRaw } from 'vue-router'
import { requiresOrgFeature } from '@/apps/settings/routes'
import { PublicFlags } from '@/types/permissions'

/**
 * Assets Module Routes
 *
 * Route Structure:
 *
 * PATH                                              | ROUTE NAME                              | VIEW
 * --------------------------------------------------|----------------------------------------|------------------------------------------
 * /assets/all/:folderId?                            | assets-all                             | AssetsAllView
 * /assets/no-workspace                              | assets-no-workspace                    | NoWorkspaceView
 * /assets/recent                                    | assets-recent                          | AssetsRecentView
 * /assets/workspaces/:workspaceId/:folderId?        | assets-workspaces-id                   | AssetsAllView
 * /assets/workspaces/:workspaceId/moderation        | assets-workspaces-id-moderation        | workspaces/ModerationView
 * /assets/workspaces/:workspaceId/recent            | assets-workspaces-id-recent            | AssetsRecentView
 * /assets/workspaces/:workspaceId                   | (WorkspaceListLayout)                  |
 *   ├── /shares                                     | assets-workspaces-id-shares            | workspaces/SharesView
 *   └── /trash                                      | assets-workspaces-id-trash             | workspaces/TrashView
 * /assets/:assetId                                  | assets-id                              | asset/OverviewView
 *   ├── /metadata                                   | assets-id-metadata                     | asset/MetadataView
 *   ├── /files                                      | assets-id-files                        | asset/FilesView
 *   ├── /files-versions                             | assets-id-files-versions               | asset/FilesVersionsView
 *   ├── /subtitles                                  | assets-id-subtitles                    | asset/SubtitlesView
 *   ├── /chapters                                   | assets-id-chapters                     | asset/ChaptersView
 *   ├── /interactivity                              | assets-id-interactivity                | asset/InteractivityView
 *   ├── /history                                    | assets-id-history                      | asset/HistoryView
 *   ├── /shares                                     | assets-id-shares                       | asset/SharesView
 *   ├── /analytics                                  | assets-id-analytics                    | (redirect)
 *   │     ├── /coverage                             | assets-id-analytics-coverage           | @analytics/CoverageView
 *   │     ├── /geography                            | assets-id-analytics-geography          | @analytics/GeographyView
 *   │     ├── /traffic-sources                      | assets-id-analytics-traffic-sources    | @analytics/TrafficSourcesView
 *   │     ├── /video-heatmap                        | assets-id-analytics-video-heatmap      | @analytics/VideoHeatmapView
 *   │     ├── /top-assets                           | assets-id-analytics-top-assets         | @analytics/TopAssetsView
 *   │     └── /reviews                              | assets-id-analytics-reviews            | @analytics/ReviewsView
 *   ├── /contents                                   | assets-id-contents                     | asset/ContentsView
 *   └── /developer                                  | assets-id-developer                    | asset/DeveloperView
 * /assets/workspaces/settings                       | assets-workspaces-settings             | (redirect)
 *   ├── /general                                    | assets-workspaces-settings-general     | (redirect → members)
 *   ├── /members                                    | assets-workspaces-settings-members     | workspaces/settings/TeamView
 *   ├── /tags                                       | assets-workspaces-settings-tags        | workspaces/settings/TagsView
 *   ├── /embed                                      | assets-workspaces-settings-embed       | workspaces/settings/EmbedView
 *   ├── /cta                                        | assets-workspaces-settings-cta         | workspaces/settings/CtaView
 *   ├── /advertising                                | assets-workspaces-settings-advertising | workspaces/settings/AdvertisingView
 *   ├── /sharing                                    | assets-workspaces-settings-sharing     | workspaces/settings/SharingView
 *   ├── /export                                     | assets-workspaces-settings-export      | workspaces/id-settings/ExportView
 *   └── /deletion-history                           | assets-workspaces-settings-deletion-history | workspaces/TrashView
 * /assets/workspaces/:workspaceId/settings          | assets-workspaces-id-settings          | (redirect)
 *   ├── /general                                    | assets-workspaces-id-settings-general  | workspaces/id-settings/GeneralView
 *   ├── /members                                    | assets-workspaces-id-settings-members  | workspaces/id-settings/TeamView
 *   ├── /tags                                       | assets-workspaces-id-settings-tags     | workspaces/id-settings/TagsView
 *   ├── /player                                     | assets-workspaces-id-settings-player   | workspaces/id-settings/PlayerView
 *   ├── /embed                                      | assets-workspaces-id-settings-embed    | workspaces/id-settings/EmbedView
 *   ├── /cta                                        | assets-workspaces-id-settings-cta      | workspaces/id-settings/CtaView
 *   ├── /advertising                                | assets-workspaces-id-settings-advertising | workspaces/id-settings/AdvertisingView
 *   ├── /shares                                     | assets-workspaces-id-settings-shares   | (redirect → assets-workspaces-id-shares)
 *   ├── /sharing                                    | assets-workspaces-id-settings-sharing  | workspaces/id-settings/SharingView
 *   ├── /export                                     | assets-workspaces-id-settings-export   | workspaces/id-settings/ExportView
 *   └── /deletion-history                           | assets-workspaces-id-settings-deletion-history | (redirect → assets-workspaces-id-trash)
 *
 * The player, embed and call-to-action tabs are the embeds domain's (`docs/plans/embeds-rust.md`
 * ER1–ER2): the watermark and the allowlist are the settings pair, the presets and the cards their
 * own resources on the `app` surface.
 */

export const assetsRoutes: RouteRecordRaw[] = [
  {
    path: 'assets',
    component: () => import('./layouts/AssetsLayout.vue'),
    children: [
      {
        path: '',
        name: 'assets',
        component: () => import('./views/AssetsRedirectView.vue'),
        meta: {
          title: 'Assets',
        },
      },
      {
        path: 'all/:folderId?',
        name: 'assets-all',
        component: () => import('./views/AssetsAllView.vue'),
        meta: {
          title: 'Assets',
        },
      },
      {
        path: 'no-workspace',
        name: 'assets-no-workspace',
        component: () => import('./views/NoWorkspaceView.vue'),
        meta: {
          title: 'Assets',
        },
      },
      {
        path: 'recent',
        name: 'assets-recent',
        component: () => import('./views/AssetsRecentView.vue'),
        meta: {
          title: 'Assets',
        },
      },
      {
        path: 'workspaces/:workspaceId/:folderId?',
        name: 'assets-workspaces-id',
        component: () => import('./views/AssetsAllView.vue'),
        props: true,
        meta: {
          title: 'Assets',
        },
      },
      // The workspace's moderation queue (`docs/plans/pool-moderation-lock.md` L9). Its static
      // segment outranks the folder parameter above whatever the order. The view is its own gate:
      // `assets.asset.moderate` is a workspace key, read from the role held there and never from
      // the organization's set a `meta.requires` checks.
      {
        path: 'workspaces/:workspaceId/moderation',
        name: 'assets-workspaces-id-moderation',
        component: () => import('./views/workspaces/ModerationView.vue'),
        props: true,
        meta: {
          title: 'Moderation',
        },
      },
      // What arrived last in a workspace, across its folders (`docs/plans/workspace-navigation.md`
      // decision 9): the library's own view, so it sits beside it rather than in the list layout.
      {
        path: 'workspaces/:workspaceId/recent',
        name: 'assets-workspaces-id-recent',
        component: () => import('./views/AssetsRecentView.vue'),
        meta: {
          title: 'Recent',
        },
      },
      // The lists that belong to a workspace, opened from its sub-entries in the drawer
      // (`docs/plans/workspace-navigation.md` decisions 4 and 5). Static segments, so they outrank
      // the folder parameter as moderation does. Each names the workspace key the backend asks
      // before it answers, and the layout sends a role without it back to the workspace: any seat
      // reads the shares, `assets.asset.trash` reads the trash.
      {
        path: 'workspaces/:workspaceId',
        component: () => import('./layouts/WorkspaceListLayout.vue'),
        children: [
          {
            path: 'shares',
            name: 'assets-workspaces-id-shares',
            component: () => import('./views/workspaces/SharesView.vue'),
            meta: { title: 'Shares' },
            // The links of one asset are that asset's Shares tab: a bookmark of the list narrowed
            // by `?contains_asset=` opens the tab.
            beforeEnter: (to) => {
              const assetId = to.query.contains_asset
              if (typeof assetId !== 'string' || assetId === '') return true
              const { workspaceId: _workspaceId, ...params } = to.params
              return { name: 'assets-id-shares', params: { ...params, assetId } }
            },
          },
          {
            path: 'trash',
            name: 'assets-workspaces-id-trash',
            component: () => import('./views/workspaces/TrashView.vue'),
            meta: { title: 'Trash', workspacePermission: 'assets.asset.trash' },
          },
        ],
      },
    ],
  },
  {
    path: 'assets/:assetId',
    props: true,
    component: () => import('./layouts/AssetDetailLayout.vue'),
    children: [
      {
        path: '',
        name: 'assets-id',
        component: () => import('./views/asset/OverviewView.vue'),
      },
      {
        path: 'metadata',
        name: 'assets-id-metadata',
        component: () => import('./views/asset/MetadataView.vue'),
      },
      {
        path: 'files',
        name: 'assets-id-files',
        component: () => import('./views/asset/FilesView.vue'),
      },
      {
        path: 'subtitles',
        name: 'assets-id-subtitles',
        component: () => import('./views/asset/SubtitlesView.vue'),
      },
      {
        path: 'chapters',
        name: 'assets-id-chapters',
        component: () => import('./views/asset/ChaptersView.vue'),
      },
      // The asset's own overlay cards (a call to action, a rating prompt) and a video's
      // recommendations strip. The workspace's and the organization's cards are edited on their own
      // settings screens.
      {
        path: 'interactivity',
        name: 'assets-id-interactivity',
        component: () => import('./views/asset/InteractivityView.vue'),
      },
      {
        path: 'analytics',
        component: () => import('./layouts/AssetAnalyticsLayout.vue'),
        props: true,
        children: [
          {
            path: '',
            name: 'assets-id-analytics',
            redirect: { name: 'assets-id-analytics-coverage' },
          },
          {
            path: 'coverage',
            name: 'assets-id-analytics-coverage',
            component: () => import('@/apps/analytics/views/CoverageView.vue'),
          },
          {
            path: 'geography',
            name: 'assets-id-analytics-geography',
            component: () => import('@/apps/analytics/views/GeographyView.vue'),
          },
          {
            path: 'traffic-sources',
            name: 'assets-id-analytics-traffic-sources',
            component: () => import('@/apps/analytics/views/TrafficSourcesView.vue'),
          },
          {
            path: 'video-heatmap',
            name: 'assets-id-analytics-video-heatmap',
            component: () => import('@/apps/analytics/views/VideoHeatmapView.vue'),
          },
          {
            path: 'top-assets',
            name: 'assets-id-analytics-top-assets',
            component: () => import('@/apps/analytics/views/TopAssetsView.vue'),
          },
          {
            path: 'reviews',
            name: 'assets-id-analytics-reviews',
            component: () => import('@/apps/analytics/views/ReviewsView.vue'),
          },
        ],
      },
      {
        path: 'contents',
        name: 'assets-id-contents',
        component: () => import('./views/asset/ContentsView.vue'),
      },
      {
        path: 'developer',
        name: 'assets-id-developer',
        component: () => import('./views/asset/DeveloperView.vue'),
      },
      // Every change to the asset's record, requested of the pool's moderators or made directly.
      {
        path: 'history',
        name: 'assets-id-history',
        component: () => import('./views/asset/HistoryView.vue'),
      },
      // Every link that has published this asset; the workspace's Shares page lists them all.
      {
        path: 'shares',
        name: 'assets-id-shares',
        component: () => import('./views/asset/SharesView.vue'),
      },
    ],
  },
  {
    path: 'assets/workspaces/settings',
    component: () => import('./layouts/WorkspacesSettingsLayout.vue'),
    children: [
      {
        path: '',
        name: 'assets-workspaces-settings',
        redirect: { name: 'assets-workspaces-settings-members' },
      },
      {
        path: 'general',
        name: 'assets-workspaces-settings-general',
        redirect: { name: 'assets-workspaces-settings-members' },
      },
      {
        path: 'members',
        name: 'assets-workspaces-settings-members',
        component: () => import('./views/workspaces/settings/TeamView.vue'),
      },
      {
        path: 'tags',
        name: 'assets-workspaces-settings-tags',
        component: () => import('./views/workspaces/settings/TagsView.vue'),
      },
      {
        path: 'embed',
        name: 'assets-workspaces-settings-embed',
        component: () => import('./views/workspaces/settings/EmbedView.vue'),
        meta: { requires: { permissions: 'tenancy.settings.manage' } },
      },
      {
        path: 'cta',
        name: 'assets-workspaces-settings-cta',
        component: () => import('./views/workspaces/settings/CtaView.vue'),
        meta: { requires: { permissions: 'tenancy.settings.manage' } },
      },
      {
        path: 'advertising',
        name: 'assets-workspaces-settings-advertising',
        component: () => import('./views/workspaces/settings/AdvertisingView.vue'),
        meta: { requires: { permissions: 'tenancy.settings.manage' } },
      },
      {
        path: 'sharing',
        name: 'assets-workspaces-settings-sharing',
        component: () => import('./views/workspaces/settings/SharingView.vue'),
        meta: { requires: { permissions: 'tenancy.settings.manage' } },
      },
      {
        // Exports are an operator's entitlement (`assets.library_exports`); every export route answers 404
        // without it. The organization's own export and its whole log are the settings' Exports page.
        path: 'export',
        name: 'assets-workspaces-settings-export',
        component: () => import('./views/workspaces/id-settings/ExportView.vue'),
        meta: { gate: requiresOrgFeature(PublicFlags.ASSETS_LIBRARY_EXPORTS) },
      },
      {
        // The organization's trash: every workspace's where the caller may restore, one row per
        // deletion. The workspace view serves it, reading no workspace off this URL.
        path: 'deletion-history',
        name: 'assets-workspaces-settings-deletion-history',
        component: () => import('./views/workspaces/TrashView.vue'),
      },
    ],
  },
  {
    path: 'assets/workspaces/:workspaceId/settings',
    props: true,
    component: () => import('./layouts/WorkspacesIdSettingsLayout.vue'),
    children: [
      {
        path: '',
        name: 'assets-workspaces-id-settings',
        redirect: { name: 'assets-workspaces-id-settings-general' },
      },
      {
        path: 'general',
        name: 'assets-workspaces-id-settings-general',
        component: () => import('./views/workspaces/id-settings/GeneralView.vue'),
      },
      {
        path: 'members',
        name: 'assets-workspaces-id-settings-members',
        component: () => import('./views/workspaces/id-settings/TeamView.vue'),
      },
      {
        path: 'tags',
        name: 'assets-workspaces-id-settings-tags',
        component: () => import('./views/workspaces/id-settings/TagsView.vue'),
      },
      {
        path: 'player',
        name: 'assets-workspaces-id-settings-player',
        component: () => import('./views/workspaces/id-settings/PlayerView.vue'),
      },
      {
        path: 'embed',
        name: 'assets-workspaces-id-settings-embed',
        component: () => import('./views/workspaces/id-settings/EmbedView.vue'),
      },
      {
        path: 'cta',
        name: 'assets-workspaces-id-settings-cta',
        component: () => import('./views/workspaces/id-settings/CtaView.vue'),
      },
      {
        path: 'advertising',
        name: 'assets-workspaces-id-settings-advertising',
        component: () => import('./views/workspaces/id-settings/AdvertisingView.vue'),
      },
      // The list left the settings for the workspace (`docs/plans/workspace-navigation.md`
      // decision 4); a bookmark keeps resolving, with its `contains_asset`.
      {
        path: 'shares',
        name: 'assets-workspaces-id-settings-shares',
        redirect: (to) => ({ name: 'assets-workspaces-id-shares', params: to.params, query: to.query }),
      },
      {
        path: 'sharing',
        name: 'assets-workspaces-id-settings-sharing',
        component: () => import('./views/workspaces/id-settings/SharingView.vue'),
      },
      {
        path: 'export',
        name: 'assets-workspaces-id-settings-export',
        component: () => import('./views/workspaces/id-settings/ExportView.vue'),
        meta: { gate: requiresOrgFeature(PublicFlags.ASSETS_LIBRARY_EXPORTS) },
      },
      // Likewise the trash (decision 5).
      {
        path: 'deletion-history',
        name: 'assets-workspaces-id-settings-deletion-history',
        redirect: (to) => ({ name: 'assets-workspaces-id-trash', params: to.params, query: to.query }),
      },
    ],
  },
]
